import { useState, useEffect, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useSiteflowData, SiteflowItem } from '@/hooks/useSiteflowData';
import { Loader2, Send, CheckCircle2, AlertCircle, ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SendToSiteflowPopoverProps {
  onConfirm: (projectId: string, familyId: string) => Promise<void>;
  sending: boolean;
  sendError?: string;
  sendSuccess?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

export default function SendToSiteflowPopover({
  onConfirm,
  sending,
  sendError,
  sendSuccess,
  disabled,
  children,
}: SendToSiteflowPopoverProps) {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [projectSearchOpen, setProjectSearchOpen] = useState(false);

  const { projects, families, loading, error: lookupError, fetchAll } = useSiteflowData();

  useEffect(() => {
    if (open && projects.length === 0 && families.length === 0 && !loading && !lookupError) {
      fetchAll();
    }
  }, [open, projects.length, families.length, loading, lookupError, fetchAll]);

  useEffect(() => {
    if (sendSuccess && open) {
      const timer = setTimeout(() => setOpen(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [sendSuccess, open]);

  const canSend = !!projectId && !!familyId && !sending;

  const childFamilies = families.filter((f) => f.parentFamily);
  const grouped = new Map<string, { parentName: string; children: SiteflowItem[] }>();
  for (const f of childFamilies) {
    const pid = f.parentFamily!.identifier;
    if (!grouped.has(pid)) {
      grouped.set(pid, { parentName: f.parentFamily!.name, children: [] });
    }
    grouped.get(pid)!.children.push(f);
  }

  const handleSend = useCallback(() => {
    if (!canSend) return;
    onConfirm(projectId, familyId);
  }, [canSend, onConfirm, projectId, familyId]);

  const displayError = sendError || lookupError;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" sideOffset={8}>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-sm">Send to Siteflow</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Select the target project and family for this flow.
            </p>
          </div>

          {displayError && (
            <div className="flex gap-2 rounded-md bg-red-50 border border-red-200 p-2.5">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-red-700">{displayError}</p>
                {lookupError && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1.5 h-7 text-xs text-red-600 hover:text-red-700 px-2"
                    onClick={fetchAll}
                  >
                    Retry
                  </Button>
                )}
              </div>
            </div>
          )}

          {sendSuccess && !displayError && (
            <div className="flex gap-2 rounded-md bg-green-50 border border-green-200 p-2.5">
              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-green-700">{sendSuccess}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : lookupError ? null : (
            <>
              {/* Project */}
              <div className="space-y-1.5">
                <Label className="text-xs">Project</Label>
                <Popover open={projectSearchOpen} onOpenChange={setProjectSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={projectSearchOpen}
                      className="w-full h-9 justify-between text-sm font-normal"
                    >
                      {projectId
                        ? (() => {
                            const p = projects.find((p) => p.identifier === projectId);
                            return p ? `${p.name}${p.type ? ` (${p.type})` : ''}` : 'Select a project...';
                          })()
                        : 'Select a project...'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search projects..." />
                      <CommandList>
                        <CommandEmpty>No project found.</CommandEmpty>
                        <CommandGroup>
                          {projects.map((p) => (
                            <CommandItem
                              key={p.identifier}
                              value={`${p.name} ${p.type ?? ''}`}
                              onSelect={() => {
                                setProjectId(p.identifier);
                                setProjectSearchOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  projectId === p.identifier ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              {p.name} {p.type ? `(${p.type})` : ''}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Family */}
              <div className="space-y-1.5">
                <Label className="text-xs">Family</Label>
                <Select value={familyId} onValueChange={setFamilyId}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select a family..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(grouped.entries()).map(([parentId, group]) => (
                      <SelectGroup key={parentId}>
                        <SelectLabel>{group.parentName}</SelectLabel>
                        {group.children.map((f) => (
                          <SelectItem key={f.identifier} value={f.identifier}>
                            {f.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Send button */}
              <Button
                onClick={handleSend}
                disabled={!canSend}
                className="w-full"
                size="sm"
              >
                {sending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send to Siteflow
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
